/**
 * Locale configuration — the single source of truth for which languages exist,
 * which is the default (served unprefixed at the root), and how URLs map to
 * locales. Pure data + helpers, safe to import on the server or client.
 */
export const locales = ["en", "sq", "ar"] as const;
export type Locale = (typeof locales)[number];

/** The default locale is served WITHOUT a path prefix (amalbryx.com/...). The
 *  others are prefixed (/sq/..., /ar/...). Keeps every existing English URL. */
export const defaultLocale: Locale = "en";

export function isRtl(locale: Locale): boolean {
  return locale === "ar";
}

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

/**
 * Build an href for a given locale: the default locale stays unprefixed, the
 * others get a /{locale} prefix. Hash-only links (#contact) and external/absolute
 * URLs pass through untouched.
 */
export function localeHref(locale: Locale, path: string): string {
  if (!path.startsWith("/")) return path; // "#contact", "mailto:", "https://"
  const stripped = path === "/" ? "" : path;
  return locale === defaultLocale ? path : `/${locale}${stripped}`;
}

/**
 * Metadata `alternates` for a page: the canonical (this locale's URL) plus an
 * hreflang entry per locale. Relative URLs resolve against the app's
 * metadataBase. Shared by every localized SSR page so canonical + hreflang stay
 * consistent with the sitemap's alternates().
 */
export function hreflangAlternates(
  locale: Locale,
  path: string,
): { canonical: string; languages: Record<string, string> } {
  const languages: Record<string, string> = {};
  for (const l of locales) languages[l] = localeHref(l, path);
  return { canonical: localeHref(locale, path), languages };
}
